import type { AgentOutboundSender } from './agentTemplate';
import type {
  ActionFamily,
  ActionTier,
  AgentConversationKind,
  AskUserOrigin,
  DoBoxInternalUndoOp,
  ExecutorAttemptKind,
  ExecutorDirective,
  ExecutorDirectiveKind,
  ExecutorItemRef,
  ExecutorLedgerKind,
  ExecutorLedgerStatus,
  ExecutorMode,
  ExecutorTrigger,
  InboxEntryLinkKind,
  ProposalDecisionRecord,
} from './doboxExecutor';
import type { OriginFlowRef } from './inboxEntries';
import type { PreparedOutcome } from './prepareDoBoxPlan';
import type { UnifiedAttachment } from './unifiedEmail';

export enum AgentRunStatus {
  PLANNING = 'PLANNING',
  RUNNING = 'RUNNING',
  WAITING_FOR_USER = 'WAITING_FOR_USER',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  STOPPED = 'STOPPED',
}

export enum AgentMessageRole {
  USER = 'user',
  ASSISTANT = 'assistant',
  TOOL = 'tool',
}

export enum AgentRunKind {
  ECHO = 'echo',
}

export enum AgentRunOrigin {
  WEB = 'WEB',
  TELEGRAM = 'TELEGRAM',
}

/**
 * Discriminator for special AgentMessage variants.
 * FLOW_REPORT — produced by a NOTIFY_ASSISTANT flow step (fire-and-forget);
 * the frontend may render this as a "flow report card" rather than a chat bubble.
 * ASK_USER_CARD — produced by an ASK_USER flow step; pauses flow execution and
 * presents a structured choice card to the user. When the user responds,
 * askUserPayload.decision and answeredAt are filled in.
 */
export enum AgentMessageKind {
  FLOW_REPORT = 'FLOW_REPORT',
  ASK_USER_CARD = 'ASK_USER_CARD',
  TEAM_REQUEST_COMPOSER = 'TEAM_REQUEST_COMPOSER',
  TEAM_REQUEST_REPLY = 'TEAM_REQUEST_REPLY',
  MEETING_POLL_INVITE = 'MEETING_POLL_INVITE',
}

/**
 * Payload embedded in AgentMessage rows with kind=ASK_USER_CARD.
 * Identifies the paused execution and step so the resolver can
 * hydrate the decision back onto the card after the user responds.
 */
export interface AskUserMessagePayload {
  executionId: string;
  stepId: string;
  options: string[];
  decision?: string;
  answeredAt?: number;
  /** FLOW (a flow's ASK_USER step) or DOBOX_EXECUTOR (the executor's own
   *  QUESTION ledger row) — which system posted this card. Folded into the
   *  sealed payload like every other field here. */
  origin?: AskUserOrigin;
  /** The executor's own question text, for an origin=DOBOX_EXECUTOR card —
   *  distinct from a flow's templated `messageTemplate`. */
  question?: string;
  /** Missing-info items the executor is asking about, for an
   *  origin=DOBOX_EXECUTOR card. */
  missingItems?: string[];
}

/**
 * Discriminates system conversations created by flow→assistant steps.
 * BACKGROUND_TASKS — the single shared system conversation per user;
 * messages from NOTIFY_ASSISTANT (BACKGROUND_TASKS_SINGLE mode) land here.
 * PER_RUN — a fresh system conversation spun up per flow execution
 * (NEW_EACH_RUN mode, used by NOTIFY_ASSISTANT with that mode and RUN_ASSISTANT).
 * REUSED_FLOW — a single shared conversation per (userId, flow), reused across
 * all executions of the same flow (RUN_ASSISTANT with REUSE_SAME mode).
 */
export enum SystemConversationKind {
  BACKGROUND_TASKS = 'BACKGROUND_TASKS',
  PER_RUN = 'PER_RUN',
  REUSED_FLOW = 'REUSED_FLOW',
}

export enum CalendarEventKind {
  CONFIRMED = 'confirmed',
  EXTERNAL = 'external',
  TASK = 'task',
  CANCELLED = 'cancelled',
  FREE = 'free',
}

/**
 * Shape of a calendar event returned by the `calendar_list_events` agent tool
 * and consumed by the frontend chat calendar item.
 * Named AgentCalendarEventData to avoid collision with the DynamoDB-level
 * CalendarEventData type in `calendar.enum.ts`.
 */
export interface AgentCalendarEventData {
  /** Google Calendar event id (instance id for recurring events). */
  eventId?: string;
  /** ISO 8601 start datetime of the event; required for PROPOSE_NEW_TIME actions. */
  startDateTime?: string;
  /** ISO 8601 end datetime of the event. */
  endDateTime?: string;
  /** Date-only start (YYYY-MM-DD) for all-day events; mutually exclusive with startDateTime. */
  startDate?: string;
  time: string;
  kind: CalendarEventKind;
  title: string;
  sub?: string;
  pill?: string;
  /** Email address of the calendar account that holds this event. Needed for execute_calendar_invite_action. */
  calendarEmail?: string;
  /** True when this event is an instance of a recurring series. */
  isRecurringInstance?: boolean;
}

export interface AgentConversation {
  conversationId: string;
  userId: string;
  /** Derived from the first prompt; truncated to 80 chars. */
  title: string;
  createdAt: number;
  lastMessageAt: number;
  /**
   * Atomic counter. The convMsgIdx of the next message equals messageCount
   * before the increment (i.e. the old value = index for the row being written).
   */
  messageCount: number;
  /** Most recently created run for this conversation; null on creation. */
  lastRunId: string | null;
  /**
   * Set when this conversation was archived (e.g. via /clear). When set, the
   * conversation is hidden from `getUserConversation` and a new active one
   * is created on demand. Rows are NEVER deleted — audit trail only.
   */
  archivedAt: number | null;
  /**
   * True when this conversation was created by a flow step (NOTIFY_ASSISTANT,
   * ASK_USER, or RUN_ASSISTANT). The dock groups these into a "Background tasks"
   * section separate from conversations the user started directly.
   * Defaults to false / undefined for user-initiated conversations.
   */
  isSystem?: boolean;
  /**
   * Discriminates system conversation subtypes. Present only when isSystem=true.
   * BACKGROUND_TASKS — the single shared per-user inbox for flow notifications
   * (NOTIFY_ASSISTANT with BACKGROUND_TASKS_SINGLE mode).
   * PER_RUN — a fresh conversation per flow execution (NEW_EACH_RUN mode).
   */
  systemConversationKind?: SystemConversationKind;
  /**
   * True while this conversation has at least one ASK_USER gate awaiting the
   * user's answer (a flow is paused on it). Set by the ASK_USER flow step when
   * it posts its choice card; cleared by respondToFlowAskUser once no
   * unanswered ASK_USER card remains. Drives the dock pill "needs action"
   * indicator and distinguishes an urgent gate from a purely informational
   * NOTIFY report. Defaults to false / undefined.
   */
  pendingUserAction?: boolean;
  /** Owner's userId while `pendingUserAction` is set, absent otherwise. Readable: it is the HASH key of the sparse pending index. */
  pendingUserActionUserId?: string;
  /** Server epoch-ms of the false -> true flip; present only while `pendingUserAction` is set. A timestamp, not PII. */
  pendingUserActionAt?: number;
  /**
   * DoBox ticket this conversation is currently linked to for agent-delegation
   * status sync (see inboxEntryDelegationSync.ts). Stamped whenever a run in
   * this conversation carries AgentRun.sourceInboxEntryRef (delegateInboxEntryToAgent,
   * answerInboxEntryDelegation, or the [dobox-context:] "Ask assistant" link),
   * so a LATER run in the same conversation that carries no ref of its own
   * (e.g. a plain chat confirm of a propose_actions proposal) can still sync
   * the ticket's card. Same shape as AgentRun.sourceInboxEntryRef.
   */
  sourceInboxEntryRef?: { taskGroupId: string; entryId: string };
  /** Structural: discriminates a DoBox-executor-driven conversation (one per
   *  (userId, entryId), every run on it executor-mode) from a normal
   *  /assistant conversation. Named `conversationKind`, not `kind`, because
   *  `kind` already names the discriminator on `AgentRunPayload`/`AgentRun`. */
  conversationKind?: AgentConversationKind;
  /** Structural: how this conversation came to be linked to
   *  `sourceInboxEntryRef` — mirrors `InboxEntryLinkKind`. */
  sourceInboxEntryLinkKind?: InboxEntryLinkKind;
  /** The flow run whose RUN_ASSISTANT step last started a run here — ids. A
   *  later run with no ref of its own (the user confirming a proposal) inherits
   *  it, the way `sourceInboxEntryRef` carries a ticket. */
  originFlowRef?: OriginFlowRef;
}

export const DEFAULT_CONVERSATION_TITLES: ReadonlySet<string> = new Set([
  '',
  'New conversation',
  'Default conversation',
]);

export interface AgentRun {
  runId: string;
  userId: string;
  conversationId: string;
  parentRunId: string | null;
  kind: AgentRunKind;
  status: AgentRunStatus;
  inputPrompt: string;
  summary: string | null;
  errorMessage: string | null;
  createdAt: number;
  updatedAt: number;
  completedAt: number | null;
  userStatusCreatedAt: string;
  /**
   * Tool names the user has pre-approved for auto-execution.
   * When `propose_actions` finds every action's toolName in this set,
   * it short-circuits and executes them inline instead of surfacing a proposal card.
   * Optional for backwards compatibility with runs created before this field existed.
   */
  autoApprovedTools?: string[];
  /**
   * When true, this run has no human watching. propose_actions will NOT surface a
   * pending proposal card for unapproved tools — instead it returns a structured
   * blocked result so the model can adapt and finish cleanly rather than hanging
   * forever waiting for a confirmation that will never arrive.
   * Undefined / false = interactive (default).
   */
  nonInteractive?: boolean;
  /** When true, the conversation is archived when the run reaches a terminal state (COMPLETED or STOPPED). */
  closeOnComplete?: boolean;
  /**
   * Channel that started this run. Absent = WEB (back-compat; nothing to
   * migrate). Only stamped when TELEGRAM so existing rows stay clean. The
   * notifier branches on this to deliver replies to Telegram.
   */
  origin?: AgentRunOrigin;
  /**
   * Set when this run was started via delegateInboxEntryToAgent (DoBox ticket
   * delegation). Lets the worker trace a run back to the ticket that spawned
   * it: it gates the ask_ticket_question tool, is forwarded onto AgentContext,
   * and drives the InboxEntry.agentDelegation status transitions (RUNNING on
   * start, PLAN_READY/DONE/FAILED on completion — see inboxEntryDelegationSync.ts).
   */
  sourceInboxEntryRef?: { taskGroupId: string; entryId: string };
  /**
   * Set when this run was delegated to a specific AgentTemplate (custom agent)
   * from delegateInboxEntryToAgent. The worker loads the template and appends
   * its systemPrompt to the run's system prompt (see handler.ts). MCP tools
   * are not scoped by this field — loadUserMcpToolsFromCache already loads
   * every CONNECTED connection the user owns regardless of run.
   */
  agentTemplateId?: string;
  /**
   * Set on completion when this run executed at least one delegated-direct
   * send (see DELEGATED_DIRECT_SEND_TOOL_NAMES, apps/api) without a confirm
   * card — the instruction looks fulfilled, so the FE may offer "Mark as
   * done" on the DoBox. Never auto-resolves the DoBox itself; the user still
   * confirms. Omitted (not false) when no such send happened this run.
   */
  suggestResolve?: boolean;
  /** Structural: how this run came to exist. Only TYPED, ANSWER and a
   *  typed-instruction DELEGATED trigger can ever qualify a tool call for
   *  AUTO-tier execution — mirrors `ExecutorTrigger`. */
  executorTrigger?: ExecutorTrigger;
  /** Plain enum, not sealed: the worker brief and the DONE settlement branch on it structurally. */
  executorAttempt?: ExecutorAttemptKind;
  /** Plain id the worker branches on: the run a RETRY re-attempts, so its DONE evidence is scoped to that run's chain. */
  executorRetryOfRunId?: string;
  /** Structural resume/approval directive carried by the USER message that
   *  started this run, forwarded so the worker can act on it without a
   *  model round-trip — mirrors `ExecutorDirective`. */
  executorDirective?: ExecutorDirective;
  /** Structural: the resolved, per-request executor mode this run actually
   *  executes under — mirrors `ExecutorMode`. */
  executorMode?: ExecutorMode;
  /** Structural: the stage segment ('prod' | 'staging') of the flag scope
   *  the API resolved when stamping `executorMode` — the staging API
   *  invokes the prod worker, so the worker needs this to know which
   *  stage's config decided its mode. */
  executorFlagStage?: string;
  /** DoBox entry this run is METERING activity for only — id. Never a
   *  delegation; unlike `sourceInboxEntryRef`, this run was not started by
   *  delegating the ticket to an agent. */
  meterInboxEntryRef?: { taskGroupId: string; entryId: string };
  /** convMsgIdx of the proposal message a CONFIRMED/CANCELLED
   *  `ProposalDecisionRecord` on this run resolves — id. */
  confirmProposalConvMsgIdx?: string;
  /** The flow run whose RUN_ASSISTANT step started this run — ids, stamped on
   *  every DoBox the run creates. */
  originFlowRef?: OriginFlowRef;
}

/**
 * Attached to a direct-send tool's success output when it executed via the
 * delegated-DoBox-instruction authorization path (no propose_actions card —
 * see withDirectSendGate / DELEGATED_DIRECT_SEND_TOOL_NAMES in
 * apps/api/src/lambda/agent/worker/delegatedDirectSend.ts). Gives the FE
 * enough to render a receipt and, for the tools that support it, an Undo
 * action — never auto-executed, always a user-initiated follow-up.
 */
export interface DelegatedSendReceipt {
  action: string;
  recipients: string[];
  when: string;
  undo?: DelegatedSendUndoHandle;
}

/** Lives only inside sealed content (AgentMessage.content's ToolResultPayload,
 *  ExecutorReceipt.undo) — never a readable attribute on its own. */
export type DelegatedSendUndoHandle =
  | { kind: 'calendar_event'; eventId: string; calendarEmail: string }
  | {
      kind: 'slack_message';
      channel: string;
      ts: string;
      workspaceId: string;
      /** Whose token posted it. Absent on handles written before bot sends existed: USER. */
      sender?: AgentOutboundSender;
    }
  | { kind: 'scheduled_email'; scheduledEmailId: string }
  | {
      kind: 'dobox_internal';
      op: DoBoxInternalUndoOp;
      taskGroupId: string;
      entryId: string;
      targetId?: string;
      previous?: Record<string, unknown>;
    };

/**
 * Payload delivered to A when B shares a response via respondToTeamRequest.
 * Embedded in AgentMessage rows with kind=TEAM_REQUEST_REPLY posted into
 * A's Background-tasks conversation.
 *
 * PRIVACY INVARIANT: this payload is written only when B explicitly clicks
 * Share. Attachments here are server-resolved snapshots; A receives nothing
 * of B's data before B's consent.
 */
export interface TeamRequestReplyPayload {
  requestId: string;
  /** B's display name — shown in A's reply card header ("Reply from <B>"). */
  fromUserName: string;
  /** The original question A's agent sent, included for context in A's card. */
  originalRequest: string;
  /**
   * Snapshots of items B chose to attach. May be empty when B replied with
   * a message only.
   */
  attachments?: import('./teamRequest').TeamRequestAttachment[];
}

/**
 * Payload embedded in AgentMessage rows with kind=TEAM_REQUEST_COMPOSER.
 * Carries the teammate request details and the agent's pre-searched finding
 * so B can review and approve before a reply is sent to A.
 *
 * PRIVACY INVARIANT: suggestedText is the content found in B's own Brain and
 * is written ONLY into B's system conversation. It MUST NOT be persisted onto
 * the TeamRequest row or sent to A without B's explicit consent.
 */
export interface TeamRequestComposerPayload {
  requestId: string;
  fromUserId: string;
  /** A's display name; shown in the card header so B knows who is asking. */
  fromUserName: string;
  /** The question A's agent sent on A's behalf. */
  requestText: string;
  /**
   * Suggested reply text seeded from B's Brain search results.
   * Empty string when no relevant Brain content was found.
   */
  suggestedText: string;
  /**
   * Status mirroring TeamRequestStatus at the time the card was posted.
   * Always AWAITING_CONSENT when first written.
   */
  status: string;
}

/**
 * A single candidate time slot within a MeetingPollInvitePayload.
 * Mirrors MeetingPollSlot from meetingPoll.ts but carried inline in the
 * AgentMessage payload so the frontend can render without a separate fetch.
 */
export interface MeetingPollInviteSlot {
  slotId: string;
  /** ISO 8601 datetime (e.g. "2026-06-01T14:00:00Z"). */
  start: string;
  /** ISO 8601 datetime. */
  end: string;
}

/**
 * Payload embedded in AgentMessage rows with kind=MEETING_POLL_INVITE.
 * Delivered to the recipient's assistant conversation by the
 * propose_meeting_times tool so they can pick a slot or decline.
 *
 * Phase 2 (frontend) renders this as a MeetingPollInviteCard.
 * Phase 3 (backend) creates the calendar event when a slot is selected.
 */
export interface MeetingPollInvitePayload {
  /** Identifier of the persisted MeetingPoll row. */
  pollId: string;
  /** Meeting subject shown in the card header. */
  title: string;
  /** Optional agenda / description. */
  description?: string;
  /** Proposed meeting duration in minutes. */
  durationMinutes: number;
  /** Display name of the initiator (A). */
  initiatorName: string;
  /** Clerk userId of the initiator — used by Phase 3 to create the event. */
  initiatorUserId: string;
  /** Candidate time slots the initiator is offering. */
  slots: MeetingPollInviteSlot[];
  /** Current poll status — always PENDING when first delivered. */
  status: string;
}

/**
 * Structured directive embedded in a USER AgentMessage when that message
 * was created by clicking an account in the communications_list account
 * picker card.
 *
 * When present, the agent worker re-runs communications_list
 * deterministically (no LLM round-trip) by merging `accountEmail` into
 * `originalListArgs`, instead of forwarding a free-text reply that the
 * model might misinterpret or hallucinate additional filters for.
 */
export interface AccountPickerResume {
  /** The email address of the account the user selected. */
  accountEmail: string;
  /**
   * The full input that was originally passed to communications_list,
   * minus any previously-selected account. Stored as a generic record
   * so this model stays decoupled from the tool's input schema and does
   * not need to be updated when new optional parameters are added.
   */
  originalListArgs: Record<string, unknown>;
}

export interface AgentMessage {
  runId: string;
  msgIdx: string;
  conversationId: string;
  convMsgIdx: string;
  role: AgentMessageRole;
  content: string;
  turn: number;
  createdAt: number;
  /** Set when this message was produced by a sub-agent (delegate_parallel). */
  subAgentId?: string;
  /** Human-readable label of the sub-agent that produced this message. */
  subAgentLabel?: string;
  /**
   * The action the user took on the draft card rendered for this message.
   * Only set on TOOL kind:'result' rows produced by the draft_compose tool.
   */
  draftAction?: AgentDraftAction;
  /** Gmail draft ID — set after createEmailDraft succeeds. */
  gmailDraftId?: string;
  /** Gmail message ID — set after sendEmail succeeds. */
  gmailMessageId?: string;
  /** Direct Gmail web URL for the draft or sent message. */
  gmailWebUrl?: string;
  /**
   * Message variant discriminator. When set to FLOW_REPORT the frontend may
   * render the message as a flow report card instead of a plain chat bubble.
   * When set to ASK_USER_CARD the frontend renders a structured choice card.
   * Absent on normal run messages.
   */
  kind?: AgentMessageKind;
  /**
   * ID of the flow that produced this message (agentId of the
   * UserCommunicationAgent). Set when kind=FLOW_REPORT or kind=ASK_USER_CARD.
   */
  sourceFlowId?: string;
  /**
   * Human-readable name of the flow that produced this message. Set when
   * kind=FLOW_REPORT or kind=ASK_USER_CARD so the frontend can render the
   * flow name without an extra fetch.
   */
  sourceFlowName?: string;
  /**
   * Structured payload for ASK_USER_CARD messages. Contains the execution and
   * step identifiers, the list of options, and — once the user has responded —
   * the chosen decision and timestamp.
   */
  askUserPayload?: AskUserMessagePayload;
  /**
   * Structured payload for TEAM_REQUEST_COMPOSER messages. Contains the
   * request details and the agent's pre-searched suggested text for B's reply.
   * Present only on messages with kind=TEAM_REQUEST_COMPOSER.
   */
  teamRequestPayload?: TeamRequestComposerPayload;
  /**
   * Structured payload for TEAM_REQUEST_REPLY messages. Delivered to A's
   * Background-tasks conversation when B shares a response. Contains B's
   * display name, the original request, and any attachment snapshots B chose
   * to send. Present only on messages with kind=TEAM_REQUEST_REPLY.
   */
  teamRequestReplyPayload?: TeamRequestReplyPayload;
  /**
   * Structured payload for MEETING_POLL_INVITE messages. Delivered to the
   * recipient's assistant conversation so they can pick a slot or decline.
   * Present only on messages with kind=MEETING_POLL_INVITE.
   */
  meetingPollInvitePayload?: MeetingPollInvitePayload;
  /**
   * Set on a USER message created by clicking an account in the
   * communications_list account picker. When present, the agent worker
   * re-runs communications_list deterministically (model-free) with
   * { ...originalListArgs, accountEmail }, instead of letting the model
   * re-interpret a free-text reply.
   *
   * Absent on all other message types.
   */
  accountPickerResume?: AccountPickerResume;
  /** Structural boolean: true when compaction must never fold this message
   *  away (a live ledger row the executor still needs to find by convMsgIdx). */
  compactionExempt?: boolean;
  /** Structural: ledger row kind, when this message is an executor ledger
   *  row — mirrors `ExecutorLedgerKind`. */
  ledgerKind?: ExecutorLedgerKind;
  /** Structural id, readable at rest: set only on a ledger row written by a
   *  setup-wizard trial run (`ExecutorTrigger.AGENT_TRIAL`) — the draft agent
   *  the trial belonged to. Absent on every real run's rows. */
  trialAgentTemplateId?: string;
  /** Structural id, readable at rest (the web branches on it): the cloud agent a message was written on
   *  behalf of, set on every row of a run that agent owns (first run, typed follow-up, approval,
   *  continuation, reply). Absent on hosted-assistant turns and on rows written before this existed. */
  agentTemplateId?: string;
  /** Structural: ledger row status — mirrors `ExecutorLedgerStatus`. */
  proposalStatus?: ExecutorLedgerStatus;
  /** Structural: action tier the ledger row was gated at — mirrors `ActionTier`. */
  tier?: ActionTier;
  /** Structural: action family the ledger row belongs to — mirrors `ActionFamily`. */
  family?: ActionFamily;
  /** Keyed HMAC digest of the action's dedup fingerprint — kept comparable
   *  so a duplicate proposal can be detected without decrypting content. */
  fingerprintHmac?: string;
  /** Keyed HMAC digest of the action's destination — kept comparable for
   *  the same reason as `fingerprintHmac`. */
  destKeyHmac?: string;
  /** Epoch ms this ledger row expires — timestamp the executor branches on
   *  to move a PENDING/OPEN row to EXPIRED. */
  expiresAt?: number;
  /** Epoch ms this ledger row was claimed for execution — timestamp
   *  guarding against a double-execute race. */
  claimedAt?: number;
  /** convMsgIdx of the instruction message this ledger row resolves — id. */
  instructionConvMsgIdx?: string;
  /** Schema version of this ledger row's shape — counter the reader
   *  branches on across shape changes. */
  ledgerSchemaVersion?: number;
  /** Epoch ms this ledger row was dismissed — timestamp the executor
   *  branches on. */
  dismissedAt?: number;
  /** Epoch ms this receipt was undone — timestamp the executor branches on. */
  undoneAt?: number;
  /** Set on an approval RECEIPT: the PROPOSAL row it settles (ids only, plain at rest). Absent on older receipts. */
  settlesRef?: ExecutorItemRef;
  /** convMsgIdx of that PROPOSAL row, for readers that key conversation rows by it. */
  settlesConvMsgIdx?: string;
  /** Bounded machine code of why an approved send failed; plain at rest, never user content. */
  failureCode?: string;
  /** libs/constants capability-gap key blocking this action — models never
   *  import libs/constants, so this stays a plain string here. */
  capabilityKey?: string;
  /** libs/constants action class blocked by the capability gap — same
   *  string-not-enum reason as `capabilityKey`. */
  blockedAction?: string;
  /** Structural boolean: true when this gap should resume the run
   *  automatically once the blocking connection is made. */
  resumeAfterConnect?: boolean;
  /** Epoch ms the resume-on-connect was armed — timestamp the resume
   *  worker branches on. */
  resumeArmedAt?: number;
  /** Counter of times this resume has fired — bounds a retry loop. */
  resumeCount?: number;
  /** Epoch ms the current resume window started — timestamp the resume
   *  worker branches on. */
  resumeWindowStartedAt?: number;
  /** Structural: directive kind of the structural resume/approval this
   *  message carries — mirrors `ExecutorDirectiveKind`. */
  executorDirectiveKind?: ExecutorDirectiveKind;
  /** Structured record of a CONFIRMED/CANCELLED decision on a batch
   *  proposal — ids and an enum only, never free text. */
  proposalDecision?: ProposalDecisionRecord;
  /** nextStepId -> `ExecutorNextStepStatus` string map — structural, read
   *  by the next-step offer UI to know which offers are still OFFERED. */
  nextStepStatuses?: Record<string, string>;
  /** Structural: outcome of a PREPARED plan once it fires or goes stale —
   *  mirrors `PreparedOutcome`. */
  preparedOutcome?: PreparedOutcome;
  /** Structural: REPLY_WAIT rows only. Wait rows sharing a group are resumed together. */
  waitGroupId?: string;
  /** Structural: REPLY_WAIT only — how many waits this delegation has armed so far, this one included. */
  waitRound?: number;
  /** Structural: REPLY_WAIT only — mirrors `AgentReplyWaitRule`, copied from the template. */
  waitRule?: string;
  /** Epoch ms the one reminder went out; absent until it does. */
  remindedAt?: number;
  /** Epoch ms of the wait's next watcher check — the readable half of its due-index row. */
  waitNextCheckAt?: number;
}

export interface AgentInterrupt {
  runId: string;
  interruptId: string;
  userId: string;
  question: string;
  schema: string;
  status: AgentInterruptStatus;
  answer: string | null;
  createdAt: number;
  answeredAt: number | null;
}

export enum AgentInterruptStatus {
  PENDING = 'PENDING',
  ANSWERED = 'ANSWERED',
  CANCELLED = 'CANCELLED',
}

export type AgentRunPayload = {
  kind: 'run';
  run: AgentRun;
};

export type AgentMessagePayload = {
  kind: 'message';
  message: AgentMessage;
  /**
   * Set when the full message exceeded the WebSocket PostToConnection size
   * limit and was replaced with a slim envelope (message.content emptied,
   * heavy optional payloads stripped). Consumers should refetch the
   * conversation instead of applying `message` directly.
   */
  oversized?: boolean;
};

export type AgentConversationPayload = {
  kind: 'conversation';
  conversationId: string;
  title: string;
  /** The row's current flag (absent on the row = false). Absent from this payload only when an older notifier sent it. */
  pendingUserAction?: boolean;
  /** Server epoch-ms of the flip to pending; sent only while pending, so a title-only change repeats the same value. */
  pendingUserActionAt?: number;
};

export type AgentEventPayload =
  | AgentRunPayload
  | AgentMessagePayload
  | AgentConversationPayload;

/**
 * Output shape of the `draft_compose` agent tool used by the `email_drafter`
 * specialty. Returned as a structured payload for the user to review — the
 * email is never sent by the agent. The frontend renders this as a draft card.
 */
export interface AgentDraftData {
  to: string;
  cc?: string;
  subject: string;
  /** Plain text body; newlines separate paragraphs. */
  body: string;
  contextLabels?: Array<{ icon: string; label: string }>;
  /** Optional follow-up note shown in the draft footer. */
  sideEffect?: string;
  /**
   * RFC 822 Message-Id header value of the message being replied to.
   * Passed through from `communication_get` so the email drafter can
   * thread the reply correctly in Gmail (In-Reply-To header).
   */
  inReplyToMessageId?: string;
  /**
   * Gmail threadId of the message being replied to. Passed as a separate
   * field in the Gmail API request payload to maintain conversation threading.
   */
  threadId?: string;
  /** Connected Gmail account that owns the thread being replied to; used as the default "From" account in the draft card. */
  fromAddress?: string;
  /**
   * Files to send with this draft (e.g. a PDF from export_artifact_pdf), already
   * downloaded and base64-encoded server-side by draft_compose. Threading this
   * through to the actual send still requires the send path to read it —
   * see LAMBDAS.md agent-worker entry for the current gap.
   */
  attachments?: UnifiedAttachment[];
}

/**
 * Action taken by the user on an email draft card rendered in chat.
 * Defined as a string-literal union (not enum) so values serialize compactly,
 * matching the same pattern as AgentCommunicationCategory.
 */
export type AgentDraftAction = 'saved' | 'sent' | 'discarded';

/**
 * Gmail category labels mapped to a terse label the model can narrate.
 * Defined as a string-literal union (not enum) so values serialize compactly
 * in tool output and require no import dance on the frontend.
 */
export type AgentCommunicationCategory =
  | 'primary'
  | 'updates'
  | 'promotions'
  | 'forums'
  | 'social';

/**
 * Shape of a single communication item returned by the `communications_list`
 * agent tool. Minimal payload tuned for the chat UI — never includes raw
 * message body / attachments / thread messages, which would balloon token
 * usage. The frontend formats `createdAtMs` against the user's timezone.
 *
 * Slack-specific fields (`channelId`, `threadKey`, `slackWorkspaceId`,
 * `isSlackDm`, `authorAvatarUrl`, `slackThreadTs`) are present only for
 * SLACK rows. `threadKey` is the stable grouping key computed by the backend —
 * the UI groups messages with the same `threadKey` into a single conversation
 * card. `slackThreadTs` is present on Slack rows that are part of a thread
 * (parent ts or reply). Used by the UI to post replies that stay in the thread.
 *
 * TEAMS rows carry `chatId` and `threadKey` instead — Teams has no thread
 * timestamp equivalent, so the chat id alone is the grouping unit.
 */
export interface AgentCommunicationData {
  id: string;
  platform: import('./messagingPlatform.enum').MessagingPlatform;
  createdAtMs: number;
  from: string;
  fromEmail?: string;
  subject?: string;
  preview: string;
  channelName?: string;
  /** True when the UNREAD label is present on this item. */
  unread: boolean;
  /** Gmail category; omitted for non-email platforms or uncategorized mail. */
  category?: AgentCommunicationCategory;
  /**
   * Email account address that holds this message (e.g. "user@gmail.com").
   * Present for GMAIL and OUTLOOK items; omitted for Slack/Teams/WhatsApp/Instagram.
   * Required by act_on_emails to route modify requests to the correct account.
   */
  userEmailAddress?: string;
  /** Slack channel id (e.g. "C01..." for channels, "D01..." for DMs). Present for SLACK rows only. */
  channelId?: string;
  /** Teams chat id. Present for TEAMS rows only; the only sanctioned source of a chat id for a reply. */
  chatId?: string;
  /**
   * Stable grouping key for the UI to cluster messages into a single conversation card.
   * Format: `<channelId>::<threadTimestamp|threadMessageId|messageId>` for SLACK,
   * `<chatId>::chat` for TEAMS. Present for SLACK and TEAMS rows only (when `channel`
   * is available on the source row). Computed by the backend — do not compute this
   * on the frontend.
   */
  threadKey?: string;
  /** Slack workspace id. Present for SLACK rows only. */
  slackWorkspaceId?: string;
  /** True when the channel is a DM or group DM; false for regular channels. Present for SLACK rows only. */
  isSlackDm?: boolean;
  /** Author avatar URL. Present when available (primarily Slack rows). */
  authorAvatarUrl?: string;
  /**
   * Slack thread timestamp (the `ts` of the parent message). Present on Slack
   * rows that are part of a thread (parent ts or reply). Used by the UI to post
   * replies that stay in the thread instead of starting a new top-level post.
   * Present for SLACK rows only.
   */
  slackThreadTs?: string;
  /**
   * True when the message was authored by the current authenticated user.
   * Currently populated for SLACK and TEAMS rows; the UI uses it to choose
   * the DM partner for tile headers (show the other participant, not the
   * user themselves).
   */
  isFromMe?: boolean;
  /**
   * Display name of the OTHER party in a Slack DM. Set on rows authored by
   * the partner (i.e. when isSlackDm is true and isFromMe is false). Used by
   * the assistant when proposing a reply card so the recipient header reads
   * correctly.
   */
  dmPartnerName?: string;
  /**
   * Review resource name (`accounts/{a}/locations/{l}/reviews/{r}`).
   * Required by gbp_reply_review. GOOGLE_BUSINESS rows only.
   */
  gbpReviewName?: string;
  /** Star rating, 1-5. Present for GOOGLE_BUSINESS review rows only. */
  gbpRating?: number;
  /**
   * Owner reply already published for this review. Present for GOOGLE_BUSINESS
   * rows that have been answered; absent means the review is unanswered.
   */
  gbpReplyText?: string;
  /** Business location the review was left on. GOOGLE_BUSINESS rows only. */
  gbpLocationName?: string;
}

// ── Communication Report types ─────────────────────────────────────────────────

/**
 * A single item in the communication report digest (needsReply or fyi section).
 * Consumed by the frontend CommunicationReport card component.
 */
export interface AgentCommunicationReportItem {
  /** messageId from the UserCommunication row. */
  id: string;
  source: 'slack' | 'email' | 'calendar';
  priority?: 'urgent' | 'mention' | 'fyi';
  /** Slack channel display name (e.g. "partnerships"). */
  channel?: string;
  /** Email org / account label. */
  org?: string;
  author: string;
  /** Hex color deterministically derived from the author's email or name. */
  authorAvatarColor: string;
  /** Local-time display string: "11:47" (today) or "Wed 16:30" (other day). */
  time: string;
  /** Email subject line. */
  subject?: string;
  /** 1-2 sentence preview, ≤ 240 chars. */
  preview: string;
  /** Optional agent reasoning shown as an italic callout line. */
  why?: string;
  /** True for email or Slack rows that can be opened in full. */
  hasFullEmail?: boolean;
  /** Number of replies in the Slack thread (for thread parent rows). */
  threadCount?: number;
}

/** One breakdown entry for the noise section. */
export interface AgentCommunicationReportNoiseBreakdown {
  label: string;
  count: number;
  /** Hex color for the breakdown chip. */
  color: string;
}

/** Aggregated noise (promotional, bot, own-message) summary. */
export interface AgentCommunicationReportNoise {
  count: number;
  /** Natural-language 1-line summary built from the breakdown. */
  summary: string;
  breakdown: AgentCommunicationReportNoiseBreakdown[];
}

/**
 * Full output of the `communication_report` agent tool.
 * The frontend renders this as a CommunicationReport card.
 */
export interface AgentCommunicationReportOutput {
  period: {
    /** ISO display of the window start, e.g. "11:32". */
    from: string;
    /** ISO display of the window end, e.g. "07:18". */
    to: string;
    /** Human-readable duration, e.g. "9h 46m". */
    duration: string;
  };
  counts: {
    mentions: number;
    dms: number;
    threadsActive: number;
    emails: number;
    /** Calendar invites — 0 for now (calendar deferred to Phase 2). */
    invites: number;
  };
  /** Items that require the user's attention / a reply. Capped at 6. */
  needsReply: AgentCommunicationReportItem[];
  /** Informational items. Capped at 8. */
  fyi: AgentCommunicationReportItem[];
  noise: AgentCommunicationReportNoise;
  error?: string;
}
